import {
  IHour,
  Restriction,
  cover24Hours,
  parseDateOnlyString,
  parseTimeOnly,
  prettyTimeOnly,
  sortRestrictions,
  AvailabilityErrorCode,
  AvailabilityErrorDetails,
  AvailabilityErrorOfficeFutureLimitDetails,
  AvailabilityErrorOfficeNotAvailableDetails,
  AvailabilityErrorResourceUnavailableDetails
} from '@gettactic/api';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { Alert } from '@/components/basic';

type OfficeClosedProps = {
  restrictions: Restriction[];
  availabilityErrors?: {
    reasonCode: AvailabilityErrorCode
  }[]
  className?: string;
  resourceId?: string | null;
};

function getDateWindowMessage(hourSince: string | null, hourUntil: string | null): string {
  const since = hourSince ? parseDateOnlyString(hourSince) : null;
  const until = hourUntil ? parseDateOnlyString(hourUntil) : null;
  const sinceFormatted = since ? format(since, 'M/dd/yy') : '';
  const untilFormatted = until ? format(until, 'M/dd/yy') : '';
  if (!since && !until) {
    return 'Always';
  }
  if (sinceFormatted === untilFormatted) {
    return sinceFormatted;
  }
  if (since && !until) {
    return sinceFormatted;
  }
  if (until && !since) {
    return untilFormatted;
  }
  return `${sinceFormatted} until ${untilFormatted}`;
}

function HourMessage({ hour }: { hour: IHour }) {
  const is24hours = cover24Hours(
    parseTimeOnly(hour.start_time),
    parseTimeOnly(hour.end_time)
  );
  const startTime = prettyTimeOnly(hour.start_time);
  const endTime = prettyTimeOnly(hour.end_time);
  const dateWindowMsg = getDateWindowMessage(hour.since, hour.until);
  const hourMessage = is24hours
    ? `all day`
    : ` between ${startTime} and ${endTime}`;
  if (hour.type === 'holiday') {
    return (
      <span>
        Holiday: {hour.name} - {dateWindowMsg}, {hourMessage}
      </span>
    );
  } else if (hour.type === 'weekday' && hour.days?.length) {
    const day = hour.days[0];
    return (
      <span>
        On <span className="capitalize">{day}</span>s the office is{' '}
        {hour.is_available ? 'only open' : 'closed'} <span>{hourMessage}</span>
      </span>
    );
  } else if (hour.type === 'rule') {
    return (
      <span>
        {dateWindowMsg} {!hour.resource_id ? 'the office' : 'the resource'} is
        closed: <span className="underline">{hour.name}</span>, {hourMessage}
      </span>
    );
  }
  return <span>Unknown reason</span>;
}

function AvailabilityErrorHourMessage({ hour }: { hour: AvailabilityErrorOfficeNotAvailableDetails | AvailabilityErrorResourceUnavailableDetails }) {
  if(!hour) {
    return null;
  }
  const is24hours = hour.is_all_day;
  const startTime = hour.start_time ? prettyTimeOnly(hour.start_time) : '';
  const endTime = hour.end_time ? prettyTimeOnly(hour.end_time) : '';
  const dateWindowMsg = getDateWindowMessage(
    hour.start_date ? `${hour.start_date}T00:00:00Z` : null,
    hour.end_date ? `${hour.end_date}T00:00:00Z` : null
  );
  const hourMessage = is24hours
    ? `all day`
    : ` between ${startTime} and ${endTime}`;
  if (hour.type === 'weekday' && hour.weekday) {
    return (
      <span>
        On <span className="capitalize">{hour.weekday}</span>s the office is{' '}
        {hour.is_open ? 'only open' : 'closed'} <span>{hourMessage}</span>
      </span>
    );
  } else if (hour.type === 'rule') {
    return (
      <span>
        {dateWindowMsg} {!(hour as AvailabilityErrorResourceUnavailableDetails).resource_id ? 'the office' : 'the resource'} is
        {hour.is_open ? ' only open' : ' closed'}: <span className="underline">{hour.name}</span>, {hourMessage}
      </span>
    );
  }
  return <span>Unknown reason</span>;
}

function RestrictionErrorRender({
  restriction: x
}: { restriction: Restriction }) {
  if (x.error === 'denied_team') {
    return (
      <p>You do not belong to any of the teams assigned to this resource.</p>
    );
  } else if (x.error === 'denied_user') {
    return <p>You have not been assigned to this resource.</p>;
  } else if (x.error === 'resource_access_not_available') {
    return <p>This resource was marked as not available by an admin.</p>;
  } else if (x.error === 'not_enough_slots') {
    return (
      <p>There's not enough space to reserve some of the selected resources.</p>
    );
  } else if (x.error === 'request_too_far_in_time') {
    return (
      <p>
        Your office admin has restricted reservations from being made this far
        in the future.
      </p>
    );
  } else if (x.error === 'office_hours_not_available' && x.office_hour) {
    return (
      <p>
        <HourMessage hour={x.office_hour}></HourMessage>
      </p>
    );
  } else if (x.error === 'resource_hours_not_available' && x.resource_hour) {
    return (
      <p>
        <HourMessage hour={x.resource_hour}></HourMessage>
      </p>
    );
  } else if (x.error === 'request_duration_cant_span_to_a_new_day') {
    // this should not happen
    console.error('Span should not be allowed by frontend validations: ', x);
    return <p>Not allowed to reserve a window time larger than 24 hs</p>;
  } else if (x.error === 'overlapping_disabled') {
    return (
      <p>
        You already have a reservation during this period with the same resource
        type.
      </p>
    );
  }
  console.error('Unrecognized error showing generic message: ', x);
  return <p>You are not allowed to reserve</p>;
}

function AvailabilityErrorRender({
  availabilityError: x
}: {
  availabilityError: {
    reasonCode: AvailabilityErrorCode,
    details?: AvailabilityErrorDetails
  }
}) {
  if (x.reasonCode === 'resource_assignment_not_allowed') {
    return (
      <p>You have not been assigned to this resource.</p>
    );
  } else if (x.reasonCode === 'resource_over_capacity') {
    return (
      <p>There's not enough space to reserve some of the selected resources.</p>
    );
  } else if (x.reasonCode === 'office_future_limit') {
    const details = x.details as AvailabilityErrorOfficeFutureLimitDetails;
    return (
      <p>
        Your office admin has restricted reservations from being made this far
        in the future. Latest allowed date is {details.latest_allowed_date}.
      </p>
    );
  } else if (x.reasonCode === 'office_not_available') {
    const details = x.details as AvailabilityErrorOfficeNotAvailableDetails;
    return (
      <p>
        <AvailabilityErrorHourMessage hour={details}></AvailabilityErrorHourMessage>
      </p>
    );
  } else if (x.reasonCode === 'resource_unavailable' && !x.details) {
    return <p>This resource was marked as not available by an admin.</p>;
  } else if (x.reasonCode === 'resource_unavailable' && x.details) {
    const details = x.details as AvailabilityErrorResourceUnavailableDetails;
    return (
      <p>
        <AvailabilityErrorHourMessage hour={details}></AvailabilityErrorHourMessage>
      </p>
    );
  }
  console.error('Unrecognized error showing generic message: ', x);
  return <p>You are not allowed to reserve</p>;
}

export default function RestrictionAlert({
  restrictions,
  availabilityErrors = [],
  className = '',
  resourceId
}: OfficeClosedProps): JSX.Element {
  const filteredRestrictions = useMemo(
    () => sortRestrictions(restrictions, resourceId),
    [restrictions, resourceId]
  );
  const hasV1Restrictions = filteredRestrictions.length > 0;
  const hasSchedulableRestrictions = availabilityErrors.length > 0;
  if (!hasV1Restrictions && !hasSchedulableRestrictions) {
    return <></>;
  }

  return (
    <div className={className}>
      <Alert
        variant="tacticAlert"
        alertMessage=""
        canDismiss={false}
        alertTitle={
          resourceId
            ? "This resource can't be reserved"
            : 'Some of the selected days have restrictions applied'
        }
        className="mt-2 px-4 py-2"
      >
        {hasV1Restrictions ?
          filteredRestrictions.map((x, idx) => (
            <RestrictionErrorRender restriction={x} key={idx} />
          ))
        : availabilityErrors.map((x, idx) => (
            <AvailabilityErrorRender availabilityError={x} key={idx} />
          ))}
      </Alert>
    </div>
  );
}
